import type * as LeasedTag from "../../lib/models/leased-tag.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { each } from "@iyk/flow"
import { getSingularOrPluralTerm } from "@iyk/localization"
import { toSentenceCase, toTemplate } from "@iyk/string"
import { Await } from "@remix-run/react"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { OwnersDialog, OwnersDialogFallback } from "./owners-dialog.tsx"

export const AsyncItemOwnersSection = ({
  team,
  itemName,
  showItemsColumn = true,
  itemOwnersPromise,
}: {
  team: {
    id: number
    name: string
    urlSlug: string
    iconUrl: string | null
  }
  itemName?: string
  showItemsColumn?: boolean
  itemOwnersPromise: ReturnType<typeof LeasedTag.getOwnersFromItem>
}) => {
  const { iykUser } = useIykUser()
  const lang = useLang()

  const avatarUrl = team.iconUrl || UI.toInitialsSVG(team.name[0])

  return (
    <React.Suspense fallback={<OwnersDialogFallback />}>
      <Await resolve={itemOwnersPromise}>
        {(itemOwners) => {
          if (itemOwners.length === 0) {
            return (
              <p className="text-sm py-1.5 text-gray-11">{toSentenceCase(lang.terms.NO_OWNERS)}</p>
            )
          }
          const isSignedInUserOwner = itemOwners.some((itemOwner) => itemOwner.id === iykUser?.id)
          const triggerLabel = getOwnershipLabel({
            itemOwnersCount: itemOwners.length,
            isSignedInUserOwner,
            lang,
          })

          return (
            <div className="flex items-center gap-2" data-test="1">
              <OwnersDialog
                team={team}
                avatar={avatarUrl}
                item={{
                  name: itemName,
                  owners: itemOwners,
                }}
                showItemsColumn={showItemsColumn}
                label={
                  <>
                    <UI.OverlapGroup>
                      {each(itemOwners.slice(0, 4), (itemOwner) => (
                        <UI.Avatar
                          variant="circle"
                          key={itemOwner.id}
                          src={itemOwner.avatarUrl || UI.defaultAvatarSVG}
                          className="size-5"
                        />
                      ))}
                    </UI.OverlapGroup>
                    {triggerLabel}
                  </>
                }
              />
            </div>
          )
        }}
      </Await>
    </React.Suspense>
  )
}

function getOwnershipLabel({
  itemOwnersCount,
  isSignedInUserOwner,
  lang,
}: {
  itemOwnersCount: number
  isSignedInUserOwner: boolean
  lang: ReturnType<typeof useLang>
}) {
  // Single owner who is the current user ("1 owner")
  if (itemOwnersCount === 1 && isSignedInUserOwner) {
    return `1 ${lang.terms.OWNER}`
  }

  // Multiple owners
  if (isSignedInUserOwner) {
    const otherOwnersCount = itemOwnersCount - 1

    // Use singular when there's only one other owner ("You and another owner")
    if (otherOwnersCount === 1) {
      return toSentenceCase(lang.terms.YOU_AND_X_OTHER_OWNERS)
    }

    // Use plural when there are multiple other owners ("You and 2 other owners")
    return toSentenceCase(
      toTemplate(lang.terms.YOU_AND_X_OTHER_OWNERS__OTHER, {
        count: otherOwnersCount.toString(),
      }),
    )
  }

  // User isn't an owner ("3 owners")
  return `${itemOwnersCount} ${getSingularOrPluralTerm(
    itemOwnersCount,
    lang.terms.OWNER,
    lang.terms.OWNER__OTHER,
    lang.name,
  )}`
}
